import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Badge,
  Skeleton,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Separator,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
} from "@asm/ui";
import {
  Globe,
  Server,
  Network,
  RefreshCw,
  ArrowLeft,
  ExternalLink,
  Clock,
  Calendar,
  History,
  ChevronRight,
  MapPin,
  Building,
  Shield,
  ShieldOff,
  Wifi,
  Lock,
  AlertTriangle,
  Cloud,
} from "lucide-react";
import { assetsApi, exceptionsApi, type AssetWithDetails, type RelatedAssets, type AssetHistoryEntry } from "@/lib/api";

// Cloud provider icons mapping
const CLOUD_PROVIDER_ICONS: Record<string, { icon: string; name: string }> = {
  aws: { icon: "/icons/aws.svg", name: "AWS" },
  amazon: { icon: "/icons/aws.svg", name: "AWS" },
  azure: { icon: "/icons/azure.svg", name: "Azure" },
  microsoft: { icon: "/icons/azure.svg", name: "Azure" },
  gcp: { icon: "/icons/gcp.svg", name: "GCP" },
  google: { icon: "/icons/gcp.svg", name: "GCP" },
  cloudflare: { icon: "/icons/cloudflare.svg", name: "Cloudflare" },
  digitalocean: { icon: "/icons/digitalocean.svg", name: "DigitalOcean" },
  linode: { icon: "/icons/linode.svg", name: "Linode" },
  akamai: { icon: "/icons/akamai.svg", name: "Akamai" },
  fastly: { icon: "/icons/fastly.svg", name: "Fastly" },
  ovh: { icon: "/icons/ovh.svg", name: "OVH" },
  hetzner: { icon: "/icons/hetzner.svg", name: "Hetzner" },
  vultr: { icon: "/icons/vultr.svg", name: "Vultr" },
  oracle: { icon: "/icons/oracle.svg", name: "Oracle Cloud" },
  ibm: { icon: "/icons/ibm.svg", name: "IBM Cloud" },
  alibaba: { icon: "/icons/alibaba.svg", name: "Alibaba Cloud" },
};

// AWS service-specific icons
const AWS_SERVICE_ICONS: Record<string, { icon: string; name: string }> = {
  cloudfront: { icon: "/icons/aws-cloudfront.svg", name: "AWS CloudFront" },
  ec2: { icon: "/icons/aws-ec2.svg", name: "AWS EC2" },
  s3: { icon: "/icons/aws-s3.svg", name: "AWS S3" },
  lambda: { icon: "/icons/aws-lambda.svg", name: "AWS Lambda" },
  elb: { icon: "/icons/aws-elb.svg", name: "AWS ELB" },
  elasticloadbalancing: { icon: "/icons/aws-elb.svg", name: "AWS ELB" },
  globalaccelerator: { icon: "/icons/aws.svg", name: "AWS Global Accelerator" },
  apigateway: { icon: "/icons/aws.svg", name: "AWS API Gateway" },
  route53: { icon: "/icons/aws.svg", name: "AWS Route 53" },
  amazon: { icon: "/icons/aws.svg", name: "AWS" },
};

function getCloudInfo(
  provider: string | null | undefined,
  service: string | null | undefined
): { icon: string; name: string } | null {
  if (!provider) return null;

  const providerKey = provider.toLowerCase().replace(/[^a-z]/g, "");

  // For AWS, check service-specific icons first
  if (providerKey === "aws" || providerKey === "amazon") {
    if (service) {
      const serviceKey = service.toLowerCase().replace(/[^a-z]/g, "");
      const serviceInfo = AWS_SERVICE_ICONS[serviceKey];
      if (serviceInfo) return serviceInfo;
    }
    return CLOUD_PROVIDER_ICONS.aws;
  }

  // For other providers
  for (const [k, v] of Object.entries(CLOUD_PROVIDER_ICONS)) {
    if (providerKey.includes(k)) return v;
  }
  return null;
}

// Simple relative time formatter
function formatRelativeTime(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffDays > 0) return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
  if (diffHours > 0) return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
  if (diffMins > 0) return `${diffMins} minute${diffMins > 1 ? "s" : ""} ago`;
  return "just now";
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const SCAN_MODULES = [
  "dns", "email", "whois", "tls", "headers", "tech", "cloud",
  "cloud_misconfig", "asn", "repos", "crawl_hints", "js_analysis",
  "favicon", "typosquat", "portscan", "service_probe",
  "subdomains_bruteforce", "ipintel", "vuln",
];

export default function AssetDetails() {
  const { id: assetId } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [excludeOpen, setExcludeOpen] = useState(false);
  const [excludeReason, setExcludeReason] = useState("");
  const [excludeAll, setExcludeAll] = useState(true);
  const [selectedModules, setSelectedModules] = useState<string[]>([]);

  const excludeMutation = useMutation({
    mutationFn: () =>
      exceptionsApi.exceptAsset(assetId!, {
        excluded_modules: excludeAll ? [] : selectedModules,
        reason: excludeReason.trim() || undefined,
      }),
    onSuccess: () => {
      setExcludeOpen(false);
      setExcludeReason("");
      setExcludeAll(true);
      setSelectedModules([]);
      queryClient.invalidateQueries({ queryKey: ["exceptions"] });
    },
  });

  const toggleModule = (mod: string) => {
    setSelectedModules((prev) =>
      prev.includes(mod) ? prev.filter((m) => m !== mod) : [...prev, mod]
    );
  };

  // Fetch asset details
  const { data: asset, isLoading: assetLoading, refetch } = useQuery({
    queryKey: ["asset", assetId],
    queryFn: () => assetsApi.get(assetId!),
    enabled: !!assetId,
  });

  // Fetch related assets
  const { data: relatedAssets, isLoading: relatedLoading } = useQuery({
    queryKey: ["asset-related", assetId],
    queryFn: () => assetsApi.getRelatedAssets(assetId!),
    enabled: !!assetId,
  });

  // Fetch asset history
  const { data: history, isLoading: historyLoading } = useQuery({
    queryKey: ["asset-history", assetId],
    queryFn: () => assetsApi.getHistory(assetId!),
    enabled: !!assetId,
  });

  const getAssetIcon = (type: string, size: "sm" | "lg" = "sm") => {
    const sizeClass = size === "lg" ? "h-8 w-8" : "h-4 w-4";
    switch (type) {
      case "subdomain":
        return <Globe className={`${sizeClass} text-blue-500`} />;
      case "ip":
        return <Server className={`${sizeClass} text-green-500`} />;
      case "port":
        return <Network className={`${sizeClass} text-purple-500`} />;
      default:
        return <Globe className={sizeClass} />;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "active":
        return <Badge variant="default" className="bg-green-500/10 text-green-600">Active</Badge>;
      case "stale":
        return <Badge variant="secondary" className="bg-yellow-500/10 text-yellow-600">Stale</Badge>;
      case "removed":
        return <Badge variant="destructive">Removed</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (assetLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <div className="grid gap-6 md:grid-cols-2">
          <Skeleton className="h-64" />
          <Skeleton className="h-64" />
        </div>
      </div>
    );
  }

  if (!asset) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
        <h2 className="text-xl font-semibold">Asset not found</h2>
        <p className="text-muted-foreground mt-2">
          The asset you're looking for doesn't exist or has been removed.
        </p>
        <Link to="/assets">
          <Button className="mt-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Assets
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Breadcrumb Navigation */}
      <nav className="flex items-center gap-2 text-sm text-muted-foreground">
        <Link to="/assets" className="hover:text-foreground transition-colors">
          Assets
        </Link>
        <span>/</span>
        <span className="text-foreground font-medium font-mono">{asset.identifier}</span>
      </nav>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/assets">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-5 w-5" />
            </Button>
          </Link>
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-muted">
            {getAssetIcon(asset.asset_type, "lg")}
          </div>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold font-mono">{asset.identifier}</h1>
              {getStatusBadge(asset.status)}
            </div>
            <p className="text-muted-foreground capitalize">{asset.asset_type}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => setExcludeOpen(true)} variant="outline" size="sm">
            <ShieldOff className="h-4 w-4 mr-2" />
            Exclude Asset
          </Button>
          <Button onClick={() => refetch()} variant="outline" size="sm">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Basic Information */}
        <Card>
          <CardHeader>
            <CardTitle>Basic Information</CardTitle>
            <CardDescription>Core asset details</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4">
              <InfoRow
                icon={<Globe className="h-4 w-4" />}
                label="Identifier"
                value={asset.identifier}
                mono
              />
              <InfoRow
                icon={<Shield className="h-4 w-4" />}
                label="Type"
                value={<span className="capitalize">{asset.asset_type}</span>}
              />
              <InfoRow
                icon={<Calendar className="h-4 w-4" />}
                label="First Seen"
                value={formatDate(asset.first_seen_at)}
              />
              <InfoRow
                icon={<Clock className="h-4 w-4" />}
                label="Last Seen"
                value={formatRelativeTime(new Date(asset.last_seen_at))}
              />
              {asset.visibility && (
                <InfoRow
                  icon={<Shield className="h-4 w-4" />}
                  label="Visibility"
                  value={<span className="capitalize">{asset.visibility}</span>}
                />
              )}
            </div>
          </CardContent>
        </Card>

        {/* Type-Specific Details */}
        <TypeSpecificDetails asset={asset} />

        {/* Related Assets */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Related Assets</CardTitle>
            <CardDescription>Parent and child assets in the hierarchy</CardDescription>
          </CardHeader>
          <CardContent>
            {relatedLoading ? (
              <div className="space-y-3">
                <Skeleton className="h-16 w-full" />
                <Skeleton className="h-16 w-full" />
              </div>
            ) : (
              <RelatedAssetsSection related={relatedAssets} />
            )}
          </CardContent>
        </Card>

        {/* Change History */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <History className="h-5 w-5" />
              Change History
            </CardTitle>
            <CardDescription>Track changes to this asset over time</CardDescription>
          </CardHeader>
          <CardContent>
            {historyLoading ? (
              <div className="space-y-3">
                {[...Array(3)].map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : !history || history.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <History className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No changes recorded yet</p>
              </div>
            ) : (
              <div className="space-y-4">
                {history.map((entry) => (
                  <HistoryEntry key={entry.id} entry={entry} />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Exclude Asset Dialog */}
      <Dialog open={excludeOpen} onOpenChange={setExcludeOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Exclude Asset</DialogTitle>
            <DialogDescription>
              Mark <span className="font-mono">{asset.identifier}</span> as not
              owned by your organization. It will be excluded from future scan
              processing.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Reason (optional)</Label>
              <textarea
                className="flex min-h-[60px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                placeholder="CDN not owned by us, third-party service..."
                value={excludeReason}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setExcludeReason(e.target.value)}
                rows={2}
              />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-2">
                <input
                  type="checkbox"
                  id="detail-exclude-all"
                  checked={excludeAll}
                  onChange={(e) => setExcludeAll(e.target.checked)}
                  className="rounded border-border"
                />
                <Label htmlFor="detail-exclude-all" className="text-sm font-normal">
                  Exclude from all modules
                </Label>
              </div>
              {!excludeAll && (
                <div className="flex flex-wrap gap-1.5 p-3 border rounded-md max-h-40 overflow-y-auto">
                  {SCAN_MODULES.map((mod) => (
                    <Badge
                      key={mod}
                      variant={selectedModules.includes(mod) ? "default" : "outline"}
                      className="cursor-pointer text-xs"
                      onClick={() => toggleModule(mod)}
                    >
                      {mod}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExcludeOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => excludeMutation.mutate()}
              disabled={
                (!excludeAll && selectedModules.length === 0) ||
                excludeMutation.isPending
              }
            >
              {excludeMutation.isPending ? "Excluding..." : "Exclude Asset"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function InfoRow({
  icon,
  label,
  value,
  mono = false,
}: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  mono?: boolean;
}) {
  return (
    <div className="flex items-center justify-between py-2">
      <div className="flex items-center gap-2 text-muted-foreground">
        {icon}
        <span className="text-sm">{label}</span>
      </div>
      <span className={`text-sm font-medium ${mono ? "font-mono" : ""}`}>{value}</span>
    </div>
  );
}

function TypeSpecificDetails({ asset }: { asset: AssetWithDetails }) {
  if (asset.subdomain) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="h-5 w-5 text-blue-500" />
            Subdomain Details
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <InfoRow
            icon={<Globe className="h-4 w-4" />}
            label="FQDN"
            value={asset.subdomain.fqdn}
            mono
          />
          <InfoRow
            icon={<Wifi className="h-4 w-4" />}
            label="Record Types"
            value={asset.subdomain.record_types.join(", ")}
          />
          {asset.subdomain.cname_target && (
            <InfoRow
              icon={<ChevronRight className="h-4 w-4" />}
              label="CNAME Target"
              value={asset.subdomain.cname_target}
              mono
            />
          )}
          {asset.subdomain.discovery_source && (
            <InfoRow
              icon={<Shield className="h-4 w-4" />}
              label="Discovery Source"
              value={asset.subdomain.discovery_source}
            />
          )}
          {asset.subdomain.takeover_risk && (
            <InfoRow
              icon={<AlertTriangle className="h-4 w-4" />}
              label="Takeover Risk"
              value={
                <Badge variant={asset.subdomain.takeover_risk === "high" ? "destructive" : "secondary"}>
                  {asset.subdomain.takeover_risk}
                </Badge>
              }
            />
          )}
          {asset.subdomain.http_status && (
            <InfoRow
              icon={<Wifi className="h-4 w-4" />}
              label="HTTP Status"
              value={asset.subdomain.http_status}
            />
          )}
          {asset.subdomain.http_title && (
            <InfoRow
              icon={<Globe className="h-4 w-4" />}
              label="Page Title"
              value={asset.subdomain.http_title}
            />
          )}
        </CardContent>
      </Card>
    );
  }

  if (asset.ip) {
    const cloudInfo = getCloudInfo(asset.ip.cloud_provider, asset.ip.cloud_region);

    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Server className="h-5 w-5 text-green-500" />
            IP Details
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <InfoRow
            icon={<Server className="h-4 w-4" />}
            label="IP Address"
            value={asset.ip.ip_address}
            mono
          />
          <InfoRow
            icon={<Shield className="h-4 w-4" />}
            label="IP Version"
            value={`IPv${asset.ip.ip_version}`}
          />
          {asset.ip.reverse_dns && (
            <InfoRow
              icon={<Globe className="h-4 w-4" />}
              label="Reverse DNS"
              value={asset.ip.reverse_dns}
              mono
            />
          )}
          {asset.ip.asn && (
            <InfoRow
              icon={<Building className="h-4 w-4" />}
              label="ASN"
              value={`AS${asset.ip.asn} - ${asset.ip.asn_name || "Unknown"}`}
            />
          )}
          {asset.ip.country_name && (
            <InfoRow
              icon={<MapPin className="h-4 w-4" />}
              label="Location"
              value={`${asset.ip.city ? asset.ip.city + ", " : ""}${asset.ip.country_name}`}
            />
          )}
          <InfoRow
            icon={
              asset.ip.is_cloud && cloudInfo ? (
                <img src={cloudInfo.icon} alt={cloudInfo.name} className="h-4 w-4" />
              ) : asset.ip.is_cloud ? (
                <Cloud className="h-4 w-4 text-blue-500" />
              ) : (
                <Server className="h-4 w-4 text-muted-foreground" />
              )
            }
            label="Infrastructure"
            value={
              asset.ip.is_cloud ? (
                <span className="text-blue-500 font-medium">
                  {cloudInfo?.name || asset.ip.cloud_provider || "Cloud"}
                </span>
              ) : (
                <span className="text-muted-foreground">
                  {asset.ip.asn_name || "On-premise / ISP"}
                </span>
              )
            }
          />
        </CardContent>
      </Card>
    );
  }

  if (asset.port) {
    // Get cloud info - prefer direct infrastructure field from service probe,
    // fall back to metadata from parent IP
    const portInfrastructure = asset.port.infrastructure;
    const cloudProvider = portInfrastructure || (asset.metadata?.cloud_provider as string | undefined);
    const cloudService = asset.metadata?.cloud_service as string | undefined;
    const isCloud = !!portInfrastructure || (asset.metadata?.is_cloud as boolean | undefined);
    const cloudInfo = getCloudInfo(cloudProvider, cloudService);

    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Network className="h-5 w-5 text-purple-500" />
            Port Details
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <InfoRow
            icon={<Network className="h-4 w-4" />}
            label="Port"
            value={`${asset.port.port}/${asset.port.protocol}`}
            mono
          />
          <InfoRow
            icon={<Shield className="h-4 w-4" />}
            label="State"
            value={<span className="capitalize">{asset.port.state}</span>}
          />
          {asset.port.service_name && (
            <InfoRow
              icon={<Wifi className="h-4 w-4" />}
              label="Service"
              value={asset.port.service_name}
            />
          )}
          {asset.port.service_product && (
            <InfoRow
              icon={<Server className="h-4 w-4" />}
              label="Product"
              value={asset.port.service_product}
            />
          )}
          {asset.port.service_version && (
            <InfoRow
              icon={<Shield className="h-4 w-4" />}
              label="Version"
              value={asset.port.service_version}
            />
          )}
          {asset.port.http_server && (
            <InfoRow
              icon={<Server className="h-4 w-4" />}
              label="Server"
              value={asset.port.http_server}
              mono
            />
          )}
          {asset.port.http_title && (
            <InfoRow
              icon={<Globe className="h-4 w-4" />}
              label="Page Title"
              value={asset.port.http_title}
            />
          )}
          {asset.port.http_technologies && asset.port.http_technologies.length > 0 && (
            <div className="pt-2">
              <p className="text-sm text-muted-foreground mb-2">Technologies</p>
              <div className="flex flex-wrap gap-2">
                {asset.port.http_technologies.map((tech, idx) => (
                  <Badge key={idx} variant="secondary" className="text-xs">
                    {tech}
                  </Badge>
                ))}
              </div>
            </div>
          )}
          {asset.port.service_banner && (
            <div className="pt-2">
              <p className="text-sm text-muted-foreground mb-2">Service Banner</p>
              <pre className="text-xs bg-muted p-3 rounded-lg overflow-x-auto font-mono">
                {asset.port.service_banner}
              </pre>
            </div>
          )}
          <InfoRow
            icon={<Lock className="h-4 w-4" />}
            label="Encrypted"
            value={asset.port.is_encrypted ? "Yes" : "No"}
          />
          {asset.port.tls_version && (
            <InfoRow
              icon={<Lock className="h-4 w-4" />}
              label="TLS Version"
              value={asset.port.tls_version}
            />
          )}
          {asset.port.probe_method && (
            <InfoRow
              icon={<Shield className="h-4 w-4" />}
              label="Probe Method"
              value={asset.port.probe_method}
            />
          )}
          {asset.port.probe_confidence != null && (
            <InfoRow
              icon={<Shield className="h-4 w-4" />}
              label="Confidence"
              value={`${asset.port.probe_confidence}%`}
            />
          )}
          <InfoRow
            icon={
              isCloud && cloudInfo ? (
                <img src={cloudInfo.icon} alt={cloudInfo.name} className="h-4 w-4" />
              ) : isCloud ? (
                <Cloud className="h-4 w-4 text-blue-500" />
              ) : (
                <Server className="h-4 w-4 text-muted-foreground" />
              )
            }
            label="Infrastructure"
            value={
              isCloud ? (
                <span className="text-blue-500 font-medium">
                  {cloudInfo?.name || cloudProvider || "Cloud"}
                </span>
              ) : (
                <span className="text-muted-foreground">On-premise / ISP</span>
              )
            }
          />
        </CardContent>
      </Card>
    );
  }

  // Generic metadata display for unknown types
  return (
    <Card>
      <CardHeader>
        <CardTitle>Additional Details</CardTitle>
      </CardHeader>
      <CardContent>
        {Object.keys(asset.metadata).length > 0 ? (
          <pre className="text-xs bg-muted p-3 rounded-lg overflow-x-auto">
            {JSON.stringify(asset.metadata, null, 2)}
          </pre>
        ) : (
          <p className="text-muted-foreground text-sm">No additional details available</p>
        )}
      </CardContent>
    </Card>
  );
}

function RelatedAssetsSection({
  related,
}: {
  related: RelatedAssets | undefined;
}) {
  if (!related || (!related.parent && related.children.length === 0)) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <Network className="h-8 w-8 mx-auto mb-2 opacity-50" />
        <p>No related assets found</p>
      </div>
    );
  }

  const getAssetIcon = (type: string) => {
    switch (type) {
      case "subdomain":
        return <Globe className="h-4 w-4 text-blue-500" />;
      case "ip":
        return <Server className="h-4 w-4 text-green-500" />;
      case "port":
        return <Network className="h-4 w-4 text-purple-500" />;
      default:
        return <Globe className="h-4 w-4" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Parent Asset */}
      {related.parent && (
        <div>
          <h4 className="text-sm font-medium text-muted-foreground mb-3">Parent Asset</h4>
          <Link to={`/assets/${related.parent.id}`}>
            <div className="flex items-center justify-between p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors cursor-pointer group">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted">
                  {getAssetIcon(related.parent.asset_type)}
                </div>
                <div>
                  <p className="font-medium font-mono text-sm">{related.parent.identifier}</p>
                  <p className="text-xs text-muted-foreground capitalize">{related.parent.asset_type}</p>
                </div>
              </div>
              <ExternalLink className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
          </Link>
        </div>
      )}

      {related.parent && related.children.length > 0 && <Separator />}

      {/* Child Assets */}
      {related.children.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-muted-foreground mb-3">
            Child Assets ({related.children.length})
          </h4>
          <div className="space-y-2">
            {related.children.map((child) => (
              <Link key={child.id} to={`/assets/${child.id}`}>
                <div className="flex items-center justify-between p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors cursor-pointer group">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted">
                      {getAssetIcon(child.asset_type)}
                    </div>
                    <div>
                      <p className="font-medium font-mono text-sm">{child.identifier}</p>
                      <p className="text-xs text-muted-foreground capitalize">{child.asset_type}</p>
                    </div>
                  </div>
                  <ExternalLink className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function HistoryEntry({ entry }: { entry: AssetHistoryEntry }) {
  const getChangeIcon = (changeType: string) => {
    switch (changeType) {
      case "created":
        return <span className="text-green-500">+</span>;
      case "updated":
        return <span className="text-blue-500">~</span>;
      case "status_changed":
        return <span className="text-yellow-500">⚡</span>;
      default:
        return <span className="text-muted-foreground">•</span>;
    }
  };

  return (
    <div className="flex items-start gap-4 p-3 rounded-lg border bg-muted/30">
      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted text-lg font-bold">
        {getChangeIcon(entry.change_type)}
      </div>
      <div className="flex-1">
        <div className="flex items-center justify-between">
          <p className="font-medium text-sm capitalize">{entry.change_type.replace(/_/g, " ")}</p>
          <span className="text-xs text-muted-foreground">
            {formatRelativeTime(new Date(entry.created_at))}
          </span>
        </div>
        {entry.field_name && (
          <p className="text-sm text-muted-foreground mt-1">
            <span className="font-mono">{entry.field_name}</span>
            {entry.old_value && entry.new_value && (
              <>
                : <span className="line-through text-red-500/70">{entry.old_value}</span>
                {" → "}
                <span className="text-green-500">{entry.new_value}</span>
              </>
            )}
          </p>
        )}
      </div>
    </div>
  );
}
